import { type ErrorInfo, StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import '@/index.css'
import { registerSessionPort } from '@/api/registerSessionPort'
import { initAnalytics, startAnalyticsContextSync } from '@/lib/analytics'
import { initSentry, startSentryContextSync } from '@/lib/sentry'
import { startAccessSync } from '@/stores/accessSync'
import { clearLegacyStreamCaches } from '@/stores/legacyCacheCleanup'
import * as Sentry from '@sentry/react'
import { App } from './App'
import { router } from './routes/routes'

initSentry(router)
startSentryContextSync()
initAnalytics()
startAnalyticsContextSync()

// Wire the transport's session port to the stores before anything can make a request.
registerSessionPort()

// Keep the cached access inputs in step with the two changes no request reports: the projects the
// user belongs to, which is the one quota counter derived from the auth user, and the active org,
// which decides whose billing status plan-gates the UI.
startAccessSync()

// Reclaim storage from superseded cache layouts. Best-effort, so it isn't awaited.
void clearLegacyStreamCaches()

// Every error boundary, the router's included, reports through these hooks with its component stack.
// The callbacks keep React's own console output, which the Sentry handler otherwise replaces.
const logRenderError = (error: unknown, errorInfo: ErrorInfo) => console.error(error, errorInfo.componentStack)
createRoot(document.getElementById('root') as HTMLElement, {
	onCaughtError: Sentry.reactErrorHandler(logRenderError),
	onUncaughtError: Sentry.reactErrorHandler(logRenderError),
}).render(
	<StrictMode>
		<App />
	</StrictMode>,
)
